/**
 * MDM Controls Component
 *
 * Purpose: Field definition selector and tenant context switcher for MDM Manager
 * Architecture: Presentational component with field selection and tenant switching
 * Dependencies: Material-UI, TenantSelector, MDM helpers
 * Author: Development Team
 * Created: 2025-01-21
 */

import React from 'react';
import { Box, SelectChangeEvent, Typography, Grid, Paper } from '@mui/material';
import { AccountTree as HierarchyIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TenantSelector from '../common/TenantSelector';
import HierarchySelect, { HierarchySelectOption } from '../common/HierarchySelect';
import { FieldDefinition } from '../../services/metadataService';
import { MetadataManagerContext } from '../../hooks/useMDMManager';

/**
 * Styled Controls Section
 */
const ControlsSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

/**
 * MDMControls Props Interface
 */
export interface MDMControlsProps {
  /**
   * Currently selected field definition ID
   */
  selectedFieldDef: string;

  /**
   * Handler for field definition change
   */
  onFieldDefChange: (event: SelectChangeEvent) => void;

  /**
   * Currently selected tenant ID
   */
  selectedTenant: string;

  /**
   * Handler for tenant context switch
   */
  onTenantChange: (tenantId: string) => Promise<void>;

  /**
   * Array of field definitions to display in dropdown
   */
  fieldDefinitions: FieldDefinition[];

  /**
   * Metadata manager context with tenant info and permissions
   */
  context: MetadataManagerContext | null;

  /**
   * Whether context switching is in progress
   */
  switchingContext?: boolean;

  /**
   * Optional test ID for automation
   */
  dataTestId?: string;
}

/**
 * MDM Controls Component
 *
 * Purpose: Provides field definition selection and tenant context switching controls
 *
 * Features:
 * - Field definition dropdown with hierarchical field indicators
 * - Tenant context switcher (when user has permission)
 * - Empty state message when no field definitions available
 * - Responsive grid layout
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <MDMControls
 *   selectedFieldDef="field-123"
 *   onFieldDefChange={handleFieldDefChange}
 *   selectedTenant="tenant-456"
 *   onTenantChange={handleTenantSwitch}
 *   fieldDefinitions={fieldDefs}
 *   context={mdmContext}
 *   switchingContext={false}
 * />
 */
export const MDMControls: React.FC<MDMControlsProps> = ({
  selectedFieldDef,
  onFieldDefChange,
  selectedTenant,
  onTenantChange,
  fieldDefinitions,
  context,
  switchingContext = false,
  dataTestId,
}) => {
  const options: HierarchySelectOption[] = fieldDefinitions.map((fieldDef) => {
    const h = fieldDef.hierarchy_config;
    if (h?.hierarchy_label) {
      return {
        value: fieldDef.fielddef_id,
        label: fieldDef.label,
        groupKey: h.hierarchy_label,
        groupLabel: h.hierarchy_label,
        level: Math.max(0, (h.hierarchy_level ?? 1) - 1),
      };
    }
    return { value: fieldDef.fielddef_id, label: fieldDef.label };
  });

  return (
    <ControlsSection data-testid={dataTestId}>
      <Grid container spacing={2} alignItems="center">
        {/* Field Definition Selection - Left Aligned */}
        <Grid size={{ xs: 12, md: 6 }}>
          <HierarchySelect
            value={selectedFieldDef}
            onChange={onFieldDefChange}
            options={options}
            label="Select Field"
            groupIcon={<HierarchyIcon fontSize="small" />}
            sx={{ minWidth: 250, maxWidth: 350 }}
            dataTestId={`${dataTestId}-field-selector`}
          />

          {/* Empty State Message */}
          {fieldDefinitions.length === 0 && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: 'block' }}
              data-testid={`${dataTestId}-empty-message`}
            >
              No list field definitions available. Master data is only applicable to list field
              types.
            </Typography>
          )}
        </Grid>

        {/* Tenant Context Switcher - Right Aligned */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {context && context.user_permissions.can_switch_context && (
              <TenantSelector
                currentTenant={selectedTenant}
                availableTenants={context.available_tenants}
                onTenantChange={onTenantChange}
                disabled={switchingContext}
                loading={switchingContext}
                size="small"
                dataTestId={`${dataTestId}-tenant-selector`}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </ControlsSection>
  );
};

export default MDMControls;
